import { apiClient } from '@/lib/apiClient';
import type { ApiResponse, Team, TeamCreate, TeamUpdate } from 'dico-types';

// ============================================================================
// TEAM API FUNCTIONS
// ============================================================================

/**
 * List all teams in a workspace.
 * @param workspaceId - The workspace ID.
 * @param signal - Optional abort signal.
 */
const getTeams = (workspaceId: string, signal?: AbortSignal) => {
  return apiClient.get<ApiResponse<Team[]>>(`/api/workspaces/${workspaceId}/teams`, {
    signal
  });
};

/**
 * Get a team by ID.
 * @param workspaceId - The workspace ID.
 * @param teamId - The team ID.
 * @param signal - Optional abort signal.
 */
const getTeamById = (workspaceId: string, teamId: string, signal?: AbortSignal) => {
  return apiClient.get<ApiResponse<Team>>(
    `/api/workspaces/${workspaceId}/teams/${teamId}`,
    { signal }
  );
};

/**
 * Create a new team.
 * @param workspaceId - The workspace ID.
 * @param data - Team creation payload.
 * @param signal - Optional abort signal.
 */
const createTeam = (
  workspaceId: string,
  data: Omit<TeamCreate, 'workspaceId'>,
  signal?: AbortSignal
) => {
  return apiClient.post<ApiResponse<Team>>(`/api/workspaces/${workspaceId}/teams`, data, {
    signal
  });
};

/**
 * Update a team.
 * @param workspaceId - The workspace ID.
 * @param teamId - The team ID.
 * @param data - Fields to update.
 * @param signal - Optional abort signal.
 */
const updateTeam = (
  workspaceId: string,
  teamId: string,
  data: TeamUpdate,
  signal?: AbortSignal
) => {
  return apiClient.put<ApiResponse<Team>>(
    `/api/workspaces/${workspaceId}/teams/${teamId}`,
    data,
    { signal }
  );
};

/**
 * Delete a team.
 * @param workspaceId - The workspace ID.
 * @param teamId - The team ID.
 * @param signal - Optional abort signal.
 */
const deleteTeam = (workspaceId: string, teamId: string, signal?: AbortSignal) => {
  return apiClient.delete<ApiResponse>(`/api/workspaces/${workspaceId}/teams/${teamId}`, {
    signal
  });
};

export const teamApi = {
  getTeams,
  getTeamById,
  createTeam,
  updateTeam,
  deleteTeam
};

export { getTeams, getTeamById, createTeam, updateTeam, deleteTeam };
